import { createContext, Dispatch } from 'react'

import { ReducerAction, IReducerContextProps } from 'utility-tools/module/reducers'
import { TOverrideType } from 'utility-tools/module/types'

interface IExtraAddressWallet {
    extraId: string | null
    extraName: string | null
}

interface IWalletAddressState {
    address: string
    showModal: boolean
    forceModal: boolean
    showStats: boolean
    addressForStats: string
    addressFieldError: string
    depositExtra: IExtraAddressWallet
    withdrawalExtra: IExtraAddressWallet
}

const initWalletAddressState = <IWalletAddressState>{
    address: '',
    showModal: false,
    forceModal: false,
    showStats: false,
    addressForStats: '',
    addressFieldError: '',
    depositExtra: { extraId: null, extraName: null },
    withdrawalExtra: { extraId: null, extraName: null },
}

enum EWalletAddressAction {
    SET_WALLET_ADDRESS = 'SET_WALLET_ADDRESS',
    SET_SHOW_MODAL = 'SET_SHOW_MODAL',
    SET_FORCE_MODAL = 'SET_FORCE_MODAL',
    SET_ADDRESS_STATS = 'SET_ADDRESS_STATS',
    SET_EXTRA_NAME = 'SET_EXTRA_NAME',
    SET_ERROR_ADDRESS_FILED = 'SET_ERROR_ADDRESS_FILED',
}

type TSetWalletAddressActionPayload = Partial<IWalletAddressState>
class SetWalletAddressAction extends ReducerAction<TSetWalletAddressActionPayload> {
    constructor(walletAddressState: TSetWalletAddressActionPayload) {
        super(EWalletAddressAction.SET_WALLET_ADDRESS, walletAddressState)
    }
}

type TSetShowModalActionPayload = boolean
class SetShowModalAction extends ReducerAction<TSetShowModalActionPayload> {
    constructor(shouldShowModal: TSetShowModalActionPayload) {
        super(EWalletAddressAction.SET_SHOW_MODAL, shouldShowModal)
    }
}

type TSetForceShowModalActionPayload = boolean
class SetForceShowModalAction extends ReducerAction<TSetForceShowModalActionPayload> {
    constructor(shouldForceShowModal: TSetForceShowModalActionPayload) {
        super(EWalletAddressAction.SET_FORCE_MODAL, shouldForceShowModal)
    }
}

type TSetAddressStatsActionPayload = { show: boolean; address: string }
class SetAddressStatsAction extends ReducerAction<TSetAddressStatsActionPayload> {
    constructor(payload: TSetAddressStatsActionPayload) {
        super(EWalletAddressAction.SET_ADDRESS_STATS, payload)
    }
}

type TSetExtraAddressFieldActionPayload = {
    depositExtraAddressField: IExtraAddressWallet
    withdrawalExtraAddressField: IExtraAddressWallet
}
class SetExtraAddressFieldAction extends ReducerAction<TSetExtraAddressFieldActionPayload> {
    constructor(extraFields: TSetExtraAddressFieldActionPayload) {
        super(EWalletAddressAction.SET_EXTRA_NAME, extraFields)
    }
}

type TSetErrorAddressFieldActionPayload = string
class SetErrorAddressFieldAction extends ReducerAction<TSetErrorAddressFieldActionPayload> {
    constructor(addressFieldError: TSetErrorAddressFieldActionPayload) {
        super(EWalletAddressAction.SET_ERROR_ADDRESS_FILED, addressFieldError)
    }
}

type TWalletAddressAction =
    | SetWalletAddressAction
    | SetShowModalAction
    | SetForceShowModalAction
    | SetAddressStatsAction
    | SetExtraAddressFieldAction
    | SetErrorAddressFieldAction

const walletAddressReducer = (
    state: IWalletAddressState,
    action: TWalletAddressAction,
): IWalletAddressState => {
    switch (action.type) {
        case EWalletAddressAction.SET_WALLET_ADDRESS: {
            const { payload } = <SetWalletAddressAction>action

            return {
                ...state,
                ...payload,
                addressFieldError: '',
            }
        }

        case EWalletAddressAction.SET_SHOW_MODAL: {
            const { payload } = <SetShowModalAction>action

            return {
                ...state,
                showModal: payload,
            }
        }

        case EWalletAddressAction.SET_FORCE_MODAL: {
            const { payload } = <SetForceShowModalAction>action

            return {
                ...state,
                showModal: payload,
                forceModal: payload,
            }
        }

        case EWalletAddressAction.SET_ADDRESS_STATS: {
            const {
                payload: { show, address },
            } = <SetAddressStatsAction>action

            return {
                ...state,
                showStats: show,
                addressForStats: address,
            }
        }

        case EWalletAddressAction.SET_EXTRA_NAME: {
            const {
                payload: { depositExtraAddressField, withdrawalExtraAddressField },
            } = <SetExtraAddressFieldAction>action

            return {
                ...state,
                depositExtra: depositExtraAddressField,
                withdrawalExtra: withdrawalExtraAddressField,
            }
        }

        case EWalletAddressAction.SET_ERROR_ADDRESS_FILED: {
            const { payload } = <SetErrorAddressFieldAction>action

            return {
                ...state,
                addressFieldError: payload,
            }
        }

        default:
            return state
    }
}

type TWalletAddressContext = TOverrideType<
    IReducerContextProps,
    {
        state: IWalletAddressState
        dispatchAction?: Dispatch<TWalletAddressAction>
    }
>

const WalletAddressContext = createContext<TWalletAddressContext>({
    state: initWalletAddressState,
})

export type { IExtraAddressWallet, IWalletAddressState }
export {
    initWalletAddressState,
    WalletAddressContext,
    walletAddressReducer,
    SetWalletAddressAction,
    SetShowModalAction,
    SetForceShowModalAction,
    SetAddressStatsAction,
    SetExtraAddressFieldAction,
    SetErrorAddressFieldAction,
}
